import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Users, 
  Ticket, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  TrendingUp,
  Settings,
  Shield,
  BarChart3,
  PieChart
} from 'lucide-react';
import dashboardService from '../services/dashboardService';

const AdminDashboard = () => {
  const { data: adminStats, isLoading, error } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => dashboardService.getAdminStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const stats = adminStats?.data || {};

  const quickActions = [
    {
      title: 'User Management',
      description: 'Manage users and permissions',
      icon: Users,
      href: '/users',
      color: 'bg-blue-600 hover:bg-blue-700 text-white'
    },
    {
      title: 'System Settings',
      description: 'Configure system settings',
      icon: Settings,
      href: '/settings',
      color: 'bg-purple-600 hover:bg-purple-700 text-white'
    },
    {
      title: 'Role Management',
      description: 'Manage roles and permissions',
      icon: Shield,
      href: '/roles',
      color: 'bg-green-600 hover:bg-green-700 text-white'
    }
  ];

  const systemStatus = [
    {
      name: 'Auto-Escalation',
      status: 'active',
      description: 'System is automatically escalating tickets'
    },
    {
      name: 'Auto-Forward',
      status: 'active', 
      description: 'Tickets are being auto-forwarded'
    },
    {
      name: 'Email Notifications',
      status: 'active',
      description: 'Email notifications are working'
    }
  ];

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Dashboard</h2>
          <p className="text-gray-600 mb-4">Error: {error.message}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-gray-600">
          System overview and administration tools
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <a
              key={action.href}
              href={action.href}
              className={`${action.color} p-6 rounded-lg transition-colors block`}
            >
              <div className="flex items-center">
                <Icon className="w-8 h-8 mr-4" />
                <div>
                  <h3 className="font-medium">{action.title}</h3>
                  <p className="text-sm opacity-90">{action.description}</p>
                </div>
              </div>
            </a>
          );
        })}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Ticket className="w-8 h-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Total Tickets</h3>
              <p className="text-2xl font-bold text-blue-600">
                {isLoading ? '...' : stats.overview?.total_tickets || 0}
              </p>
              <p className="text-sm text-gray-500">
                {stats.overview?.active_tickets || 0} active
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="w-8 h-8 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Total Users</h3>
              <p className="text-2xl font-bold text-green-600">
                {isLoading ? '...' : stats.overview?.total_users || 0}
              </p>
              <p className="text-sm text-gray-500">
                {stats.user_stats?.length || 0} user types
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="w-8 h-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">SLA Compliance</h3>
              <p className="text-2xl font-bold text-purple-600">
                {isLoading ? '...' : stats.sla_compliance?.percentage || 0}%
              </p>
              <p className="text-sm text-gray-500">
                {stats.sla_compliance?.on_time || 0} of {stats.sla_compliance?.total || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="w-8 h-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Escalation Rate</h3>
              <p className="text-2xl font-bold text-orange-600">
                {isLoading ? '...' : stats.escalation_rate || 0}%
              </p>
              <p className="text-sm text-gray-500">
                Last 30 days
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Time Analytics */}
      {stats.time_analytics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Today</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">New Tickets</span>
                <span className="font-semibold">{stats.time_analytics.today?.new_tickets || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Resolved</span>
                <span className="font-semibold text-green-600">{stats.time_analytics.today?.resolved_tickets || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">This Week</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">New Tickets</span>
                <span className="font-semibold">{stats.time_analytics.this_week?.new_tickets || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Resolved</span>
                <span className="font-semibold text-green-600">{stats.time_analytics.this_week?.resolved_tickets || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">This Month</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">New Tickets</span>
                <span className="font-semibold">{stats.time_analytics.this_month?.new_tickets || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Resolved</span>
                <span className="font-semibold text-green-600">{stats.time_analytics.this_month?.resolved_tickets || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Status */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">System Status</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {isLoading ? (
              <div className="col-span-3 text-center">Loading system status...</div>
            ) : (
              stats.system_status?.map((item) => (
                <div key={item.name} className="flex items-center p-4 border border-gray-200 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className={`w-3 h-3 rounded-full ${
                      item.status === 'active' ? 'bg-green-500' : 
                      item.status === 'inactive' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                    <p className="text-xs text-gray-500 capitalize">{item.status}</p>
                  </div>
                </div>
              )) || [
                {name: 'Auto-Escalation', status: 'inactive', description: 'System status unavailable'},
                {name: 'Auto-Forward', status: 'inactive', description: 'System status unavailable'},
                {name: 'Email Notifications', status: 'inactive', description: 'System status unavailable'}
              ].map((item) => (
                <div key={item.name} className="flex items-center p-4 border border-gray-200 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tickets by Status */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <BarChart3 className="w-5 h-5 text-gray-500 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Tickets by Status</h2>
            </div>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.ticket_stats?.by_status?.map((stat) => (
                  <div key={stat.status} className="flex justify-between items-center">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {stat.status.replace('_', ' ').replace('-', ' ')}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({stat.percentage}%)
                      </span>
                    </div>
                    <span className="text-sm text-gray-600 font-semibold">{stat.count}</span>
                  </div>
                )) || <p className="text-gray-500">No ticket data available</p>}
              </div>
            )}
          </div>
        </div>

        {/* Users by Type */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <PieChart className="w-5 h-5 text-gray-500 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Users by Type</h2>
            </div>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.user_stats?.map((stat) => (
                  <div key={stat.user_type} className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-900">{stat.user_type}</span>
                    <span className="text-sm text-gray-600 font-semibold">{stat.count}</span>
                  </div>
                )) || <p className="text-gray-500">No user data available</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tickets by Priority (if available) */}
      {stats.ticket_stats?.by_priority && stats.ticket_stats.by_priority.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Tickets by Priority</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.ticket_stats.by_priority.map((stat) => (
                <div key={stat.priority} className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{stat.count}</div>
                  <div className="text-sm text-gray-600 capitalize">{stat.priority}</div>
                  <div className="text-xs text-gray-500">{stat.percentage}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trends Chart (if available) */}
      {stats.trends && (stats.trends.tickets_trend?.length > 0 || stats.trends.resolution_trend?.length > 0) && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">7-Day Trends</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {stats.trends.tickets_trend && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">New Tickets</h3>
                  <div className="space-y-2">
                    {stats.trends.tickets_trend.map((trend) => (
                      <div key={trend.date} className="flex justify-between text-sm">
                        <span className="text-gray-600">{trend.date}</span>
                        <span className="font-medium">{trend.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {stats.trends.resolution_trend && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Resolved Tickets</h3>
                  <div className="space-y-2">
                    {stats.trends.resolution_trend.map((trend) => (
                      <div key={trend.date} className="flex justify-between text-sm">
                        <span className="text-gray-600">{trend.date}</span>
                        <span className="font-medium text-green-600">{trend.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;